import { useCallback, useMemo, useState } from "react";
import { useToast } from "../../../Hooks/ToasContext";
import { ListCollaboratorsUseCase } from "../../../Usecase/Collaborators/list-collaborators-usecase";
import { ICollaboratorEntity } from "../../../Services/api/collaborators/collaborators.interface";
import { DestroyCollaboratorsUseCase } from "../../../Usecase/Collaborators/destroy-collaborators-usecase";
import { ShowCollaboratorsUseCase } from "../../../Usecase/Collaborators/show-collaborators-usecase";
import { ResetCollaboratorPasswordUseCase } from "../../../Usecase/Collaborators/reset-collaborator-password-usecase";
import { IListSearchPayload } from "../../../Components/List/list.types";
import { normalizeCollaboratorEntity } from "../../../utils/collaborator-permissions";

interface IuseListCollaboratorsProps {
  listCollaboratorsUseCase: ListCollaboratorsUseCase
  destroyCollaboratorsUseCase: DestroyCollaboratorsUseCase
  showCollaboratorsUseCase?: ShowCollaboratorsUseCase
  resetCollaboratorPasswordUseCase?: ResetCollaboratorPasswordUseCase
  onlyHCP?: boolean
}

export function useListCollaborators({ 
    listCollaboratorsUseCase, 
    destroyCollaboratorsUseCase,
    showCollaboratorsUseCase,
    resetCollaboratorPasswordUseCase
}: IuseListCollaboratorsProps){
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [data, setData] = useState<ICollaboratorEntity[]>([])
    const [totalRegisters, setTotalRegisters] = useState(0)
    const [collaboratorToDestroy, setCollaboratorToDestroy] = useState('')
    const [collaboratorToResetPassword, setCollaboratorToResetPassword] = useState('')
    const [currentSearch, setCurrentSearch] = useState('')
    const { showToast } =  useToast()

    const listCollaborators = useCallback(async ({ page: currentPage = 1, search = "" }: { page?: number, search?: string } = {}) => {
        try {
          setLoading(true)
          setLoadError(false)
          
          const listResult = await listCollaboratorsUseCase.handle({ page: currentPage, searchBy: search});

          if (listResult.isFailure) {
            setLoadError(true)
            const errorMessage = listResult?.error?.error || 'Erro de autorização no servidor (403)';
            showToast(String(errorMessage), 'error');
            return;
          }

         const list = listResult.getValue()

         if(list){
            const normalized = list.data.map((colab) => normalizeCollaboratorEntity(colab))
            setData(normalized)
            setTotalRegisters(list.total)
            setLoadError(false)
         }

         setCurrentSearch(search)
        } catch (error) {
          setLoadError(true)
          console.error("error listCollaborators function: ", error)
        } finally {
          setLoading(false);
        }
      }, [listCollaboratorsUseCase, showToast]);

      const handleResetCollaboratorPassword = async (collaboratorId:string) => {
        if (!resetCollaboratorPasswordUseCase) return
        
        try {
          setLoading(true)
          
          const listResult = await resetCollaboratorPasswordUseCase.handle(collaboratorId);

          if (listResult.isFailure) {
            showToast(String(listResult?.error?.error), 'error');
            return;
          }

          showToast('Senha resetada com sucesso', 'success')
          setCollaboratorToResetPassword('')
          listCollaborators({ page: 1, search: currentSearch })
        } catch (error) {
          console.error("error resetCollaboratorPassword function: ", error)
        } finally {
          setLoading(false);
        }
      }
    

    const handleDestroyCollaborator = async () => {
      try {
        setLoading(true)
        
        const listResult = await destroyCollaboratorsUseCase.handle(collaboratorToDestroy);

        if (listResult.isFailure) {
          showToast(String(listResult?.error?.error), 'error');
          return;
        }

        showToast('Colaborador Removido', 'success')
        listCollaborators({ page: 1, search: currentSearch })
        setCollaboratorToDestroy('')
      } catch (error) {
        console.error("error destroyCollaborator function: ", error)
      } finally {
        setLoading(false);
      }
    };

    const handleSearch = useCallback(({ page, searchBy }: IListSearchPayload) => {
      listCollaborators({ page, search: searchBy })
    }, [listCollaborators]);

    const handlePageChange = useCallback(({ page, searchBy }: IListSearchPayload) => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      listCollaborators({ page, search: searchBy ?? currentSearch })
    }, [listCollaborators, currentSearch]);

    const list = useMemo(() => {
        return data?.map(colab => ({ ...colab })) || []
    }, [data])

    const collaboratorToDestroyName = useMemo(() => {
      if (!collaboratorToDestroy) return ''
      return data.find((item) => item.id === collaboratorToDestroy)?.name || 'este colaborador'
    }, [collaboratorToDestroy, data])

    const collaboratorToResetPasswordName = useMemo(() => {
      if (!collaboratorToResetPassword) return ''
      return data.find((item) => item.id === collaboratorToResetPassword)?.name || 'este colaborador'
    }, [collaboratorToResetPassword, data])

    return {
        loading,
        loadError,
        listCollaborators,
        list,
        data,
        collaboratorToDestroy,
        collaboratorToDestroyName,
        setCollaboratorToDestroy,
        collaboratorToResetPassword,
        collaboratorToResetPasswordName,
        setCollaboratorToResetPassword,
        handleResetCollaboratorPassword,
        handleDestroyCollaborator,
        totalRegisters,
        handleSearch,
        handlePageChange,
        currentSearch,
    }
}
